import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { ROUTES } from '@/config/routes';
import type { UserRole } from '@/types';
import { WorkspaceContext, type WorkspaceContextValue } from './workspaceContextValue';

function resolveRoleFromPath(pathname: string, fallback: UserRole): UserRole {
  if (pathname.includes('/user/operator/')) return 'operator';
  if (pathname.includes('/user/novice/')) return 'novice';
  return fallback;
}

function resolveSite(role: UserRole, userSite: string): string {
  if (role === 'novice') return 'First floor';
  return userSite;
}

export function WorkspaceProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const location = useLocation();

  const value = useMemo<WorkspaceContextValue | null>(() => {
    if (!user) return null;

    const effectiveRole = resolveRoleFromPath(location.pathname, user.role);
    const site = resolveSite(effectiveRole, user.site);

    return {
      effectiveRole,
      effectiveUser: { ...user, role: effectiveRole, site },
      workspaceRoute:
        effectiveRole === 'operator' ? ROUTES.OPERATOR_WORKSPACE : ROUTES.NOVICE_WORKSPACE,
      canCreateUsers: true,
    };
  }, [location.pathname, user]);

  if (!value) return null;

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}
